import { useState } from 'react';
import { PropTypes } from 'prop-types';
import ReCAPTCHA from 'react-google-recaptcha';
import { Form, Row, Col, Tooltip, Input, Button, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import CustomPasswordTooltip from 'components/CustomPassowrdTooltip/CustomPassowrdTooltip';
import { validateMobile } from 'components/CommonValidations/CommonValidations';

function MerchantSignUpForm({
  merchantSignUpForm,
  handleFormSubmit,
  submitButtonDisable,
  recaptchaRef
}) {
  const [merchantPasswordTooltipOpen, setMerchantPasswordTooltipOpen] = useState(false);

  return (
    <Form
      style={{ display: 'flex', justifyContent: 'center' }}
      form={merchantSignUpForm}
      name="new-sign-up"
      layout="vertical"
      onFinish={handleFormSubmit('merchant')}
      autoComplete="off"
    >
      <Row gutter={[12, 12]}>
        {/* BRAND NAME */}
        <Col span={24}>
          <Form.Item
            name="merchant_name"
            label={
              <>
                <Typography.Text style={{ fontSize: '16px' }}>Brand Name</Typography.Text>
                <span className="required-mark" />
                <Tooltip title="Doing Business As (DBA) Name">
                  <InfoCircleOutlined style={{ color: '#A0A0A0', marginLeft: '5px' }} />
                </Tooltip>
              </>
            }
            rules={[
              { required: true, message: 'Please enter Brand Name' },
              {
                validator(_, value) {
                  if (value && !/^[a-zA-Z0-9.&-]*[a-zA-Z0-9][a-zA-Z0-9 .&-]*$/g.test(value)) {
                    return Promise.reject(new Error('Please enter a valid Brand Name.'));
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Input style={{ width: '100%', padding: '12px' }} placeholder="Enter brand name" />
          </Form.Item>
        </Col>

        {/* BUSINESS EMAIL */}
        <Col span={24}>
          <Form.Item
            style={{ marginBottom: '0px' }}
            name="merchant_email"
            label={
              <>
                <Typography.Text style={{ fontSize: '16px' }}>Business Email ID</Typography.Text>
                <span className="required-mark" />
                <Tooltip title="This email ID will be verified & will be used for further communication">
                  <InfoCircleOutlined style={{ color: '#A0A0A0', marginLeft: '5px' }} />
                </Tooltip>
              </>
            }
            rules={[
              { required: true, message: 'Please enter business email ID' },
              {
                validator(_, value) {
                  if (value && !/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/g.test(value)) {
                    return Promise.reject(new Error('Please enter a valid Business Email ID.'));
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Input
              style={{ width: '100%', padding: '12px' }}
              placeholder="Enter Business Email ID"
              autoComplete="off"
            />
          </Form.Item>
        </Col>

        {/* MOBILE NUMBER */}
        <Col span={24}>
          <Form.Item
            name="merchant_phone"
            label={
              <>
                <Typography.Text style={{ fontSize: '16px' }}>Mobile Number</Typography.Text>
                <span className="required-mark" />
                <Tooltip title="This mobile number will be used for further communication">
                  <InfoCircleOutlined style={{ color: '#A0A0A0', marginLeft: '5px' }} />
                </Tooltip>
              </>
            }
            rules={[
              { required: true, message: 'Please enter Mobile Number' },
              {
                validator: (_, value) => validateMobile(value)
              }
            ]}
          >
            <Input
              style={{ width: '100%', padding: '12px' }}
              placeholder="Enter 10 digit mobile number"
            />
          </Form.Item>
        </Col>

        {/* PASSWORD */}
        <Col span={24}>
          <Form.Item
            name="merchant_password"
            label={
              <>
                <Typography.Text style={{ fontSize: '16px' }}>Create Password</Typography.Text>
                <span className="required-mark" />
              </>
            }
            rules={[{ required: true, message: 'Please enter password' }]}
          >
            <CustomPasswordTooltip
              placeholder="Enter Password"
              datakey="merchant_password"
              formData={merchantSignUpForm}
              setTooltipOpen={setMerchantPasswordTooltipOpen}
              tooltipOpen={merchantPasswordTooltipOpen}
            />
          </Form.Item>
        </Col>

        {/* GST / PAN */}
        <Col span={24}>
          <Form.Item
            name="merchant_gstin_or_pan"
            label={
              <>
                <Typography.Text style={{ fontSize: '16px' }}>
                  GSTIN or Business PAN
                </Typography.Text>
                <span className="required-mark" />
                <Tooltip title="In case of proprietorship & individual entity, upload personal PAN">
                  <InfoCircleOutlined style={{ color: '#A0A0A0', marginLeft: '5px' }} />
                </Tooltip>
              </>
            }
            rules={[
              { required: true, message: 'Please enter gst or pan' },
              {
                validator(_, value) {
                  if (value) {
                    if (
                      /^\d{2}[A-Z]{4,5}\d{4,5}[A-Z][A-Z\d][ZDC][A-Z\d]$/.test(value) ||
                      /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value)
                    ) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error('Please enter a valid GSTIN or Business PAN Number.')
                    );
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Input style={{ width: '100%', padding: '12px' }} placeholder="Enter GSTIN or PAN" />
          </Form.Item>
        </Col>

        {/* REF CODE */}
        <Col span={24}>
          <Form.Item
            name="ref_code"
            label={
              <>
                <Typography.Text style={{ fontSize: '16px' }}>Referral Code</Typography.Text>
                <Tooltip title="Referral Code passed automatically">
                  <InfoCircleOutlined style={{ color: '#A0A0A0', marginLeft: '5px' }} />
                </Tooltip>
              </>
            }
          >
            <Input
              disabled
              style={{ width: '100%', padding: '12px' }}
              placeholder="Referral Code"
            />
          </Form.Item>
        </Col>

        <ReCAPTCHA
          ref={recaptchaRef}
          size="invisible"
          sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
        />

        <Col span={24} style={{ display: 'flex', justifyContent: 'center' }}>
          <Form.Item>
            <Button type="primary" htmlType="submit" disabled={submitButtonDisable}>
              Submit
            </Button>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
}

MerchantSignUpForm.propTypes = {
  merchantSignUpForm: PropTypes.instanceOf(Object).isRequired,
  handleFormSubmit: PropTypes.func.isRequired,
  submitButtonDisable: PropTypes.bool.isRequired,
  recaptchaRef: PropTypes.instanceOf(Object).isRequired
};
export default MerchantSignUpForm;
